import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Alert } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';

function Welcome() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const result = await authService.checkAuth();
    if (result.authenticated) {
      setUser(result.user);
    } else {
      navigate('/login');
    }
    setLoading(false);
  };

  const handleQuickStart = async () => {
    // Mark path choice and welcome as completed
    await businessService.saveField('onboarding_path', 'quick_start');
    await businessService.saveField('welcome_completed', 'true');
    navigate('/quick-start');
  };

  const handleGuidedJourney = async () => {
    // Mark path choice - but don't mark welcome as completed yet
    // The analyze-business page will do that after user confirms or skips
    await businessService.saveField('onboarding_path', 'guided_journey');
    navigate('/analyze-business');
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={10} lg={10}>
          <Card className="shadow-lg">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <h1 className="display-4 mb-3">Welcome to AI Profit Engineer</h1>
                <p className="lead">
                  Let's build your roadmap to profit. <strong>Choose your path:</strong>
                </p>
              </div>

              <Alert variant="info" className="mb-4 text-center">
                <strong>{user?.email}</strong> — Both paths lead to your Exit Strategy and Battle Plan.
                The difference is how deep you want to go right now.
              </Alert>

              <Row className="mb-4">
                {/* Quick Start Path */}
                <Col md={6} className="mb-4">
                  <Card className="h-100" style={{ borderWidth: '2px', borderColor: '#a10000' }}>
                    <Card.Header className="text-white text-center py-3" style={{ backgroundColor: '#a10000' }}>
                      <h4 className="mb-0">Quick Start</h4>
                      <small>~15 minutes</small>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column">
                      <p className="text-muted mb-3">
                        See your Exit Strategy and Battle Plan <strong>fast</strong>. Perfect if you want to see the dream first.
                      </p>

                      <ul className="mb-4">
                        <li className="mb-2">Enter your website URL</li>
                        <li className="mb-2">Quick AI conversation (~5-8 exchanges)</li>
                        <li className="mb-2">Verify your products & pricing</li>
                        <li className="mb-2">Get your personalized Exit Strategy</li>
                      </ul>

                      <div className="mt-auto">
                        <Alert variant="light" className="py-2 small mb-3">
                          <strong>Best for:</strong> Seeing what's possible before investing more time. You can always come back and do the Guided Journey later.
                        </Alert>

                        <div className="d-grid">
                          <Button
                            size="lg"
                            className="btn-brand-primary"
                            onClick={handleQuickStart}
                          >
                            Start Quick Path →
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Guided Journey Path */}
                <Col md={6} className="mb-4">
                  <Card className="h-100" style={{ borderWidth: '2px', borderColor: '#cca557' }}>
                    <Card.Header className="text-center py-3" style={{ backgroundColor: '#cca557', color: '#0a0a0a' }}>
                      <h4 className="mb-0">Guided Journey</h4>
                      <small>~6-8 hours (can pause anytime)</small>
                    </Card.Header>
                    <Card.Body className="d-flex flex-column">
                      <p className="text-muted mb-3">
                        Build your complete strategic foundation <strong>step-by-step</strong>. The full experience.
                      </p>

                      <ul className="mb-4">
                        <li className="mb-2">Enter your website URL to pre-fill your profile</li>
                        <li className="mb-2">Deep AI conversations at every step</li>
                        <li className="mb-2">Customer personas & journey mapping</li>
                        <li className="mb-2">Brand identity & voice development</li>
                        <li className="mb-2">Complete marketing foundation</li>
                        <li className="mb-2">Exit Strategy with full context</li>
                      </ul>

                      <div className="mt-auto">
                        <Alert variant="light" className="py-2 small mb-3">
                          <strong>Best for:</strong> Doing it right the first time. Build a rock-solid strategic foundation that powers everything else.
                        </Alert>

                        <div className="d-grid">
                          <Button
                            size="lg"
                            className="btn-brand-success"
                            onClick={handleGuidedJourney}
                          >
                            Start Guided Journey →
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div className="text-center">
                <p className="text-muted small mb-2">
                  <strong>Not sure?</strong> Start with Quick Start to see your Exit Strategy, then come back for the Guided Journey whenever you're ready.
                </p>
                <Button
                  variant="link"
                  onClick={() => navigate('/dashboard')}
                  className="text-muted"
                >
                  Skip for now (go to Dashboard)
                </Button>
              </div>

            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default Welcome;
